/*
  Booking pages v2 (booking-block, booking-cta, booking-confirmation).
  Plan: docs/plans/2026-09-25-1540-feat-booking-pages-v2-build-plan.md (KTD1 to KTD5, KTD10, KTD11).
  One bundle, defined once per page; init is idempotent per container (data-booking-init) so
  shopify:section:load can run it again for one section. Work is found through DOM markers only.
*/
(function () {
  'use strict';
  if (window.BookingV2) return;

  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var BV = window.BookingV2 = { version: '1.2.1', design: DESIGN, blocks: [], marks: { bundleInit: performance.now() }, events: [] };
  var READY_TIMEOUT = 8000;   // ms after the controller starts without Appointo's div (KTD1)
  var OPEN_TIMEOUT = 20000;   // ms after a tap without a visible modal (KTD1; raised from 10 s after the Slow 3G run measured first-tap calendar loads above 10 s)

  /* ---------- analytics (KTD11): one push helper, fire-once registry per event name ---------- */
  var fired = {};
  BV.track = function (name, params, opts) {
    if (DESIGN) return false;
    opts = opts || {};
    if (opts.once && fired[name]) return false;
    fired[name] = true;
    var payload = { event: name };
    Object.keys(params || {}).forEach(function (k) { if (params[k] !== undefined && params[k] !== null) payload[k] = params[k]; });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    return true;
  };

  /* ---------- small helpers ---------- */
  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    var s = getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden') return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }
  function urlWithVariant(id) {
    var u = new URL(location.href);
    if (id) u.searchParams.set('variant', id); else u.searchParams.delete('variant');
    return u.toString();
  }
  function replaceVariant(id) {
    if (DESIGN) return;
    history.replaceState(history.state, '', urlWithVariant(id));
  }
  function currentVariantParam() {
    return new URL(location.href).searchParams.get('variant');
  }
  var listeners = {};
  BV.on = function (name, fn) { (listeners[name] = listeners[name] || []).push(fn); };
  function emit(name, detail) {
    BV.events.push([name, Math.round(performance.now()), detail]);
    (listeners[name] || []).forEach(function (fn) { try { fn(detail); } catch (e) { /* never break the page */ } });
  }

  /* ---------- modal detection (KTD4): #appointo-modal is injected into body on the first tap ---------- */
  var modal = { el: null, open: false, opens: 0, observer: null, bodyObserver: null };
  function modalVisible() { return isVisible(document.getElementById('appointo-modal')); }
  function syncModal(silent) {
    var now = modalVisible();
    if (now !== modal.open) {
      modal.open = now;
      if (now) { if (!silent) modal.opens += 1; emit('modal-open', { count: modal.opens, restored: !!silent }); }
      else emit('modal-close', {});
    }
  }
  function watchModal() {
    if (DESIGN || modal.bodyObserver) return;
    var attach = function () {
      var el = document.getElementById('appointo-modal');
      if (!el || el === modal.el) return;
      modal.el = el;
      if (modal.observer) modal.observer.disconnect();
      modal.observer = new MutationObserver(function () { syncModal(false); });
      modal.observer.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      syncModal(false);
    };
    modal.bodyObserver = new MutationObserver(function () { attach(); if (modal.el && !modal.el.isConnected) { modal.el = null; syncModal(false); } });
    modal.bodyObserver.observe(document.body, { childList: true });
    attach();
  }
  BV.modalOpen = function () { return modal.open; };
  BV.modalCount = function () { return modal.opens; };

  /* ---------- booking block: cards and variant sync (KTD2, KTD3) ---------- */
  function initBlock(root) {
    if (root.hasAttribute('data-booking-init')) return;
    root.setAttribute('data-booking-init', '');

    var onProduct = root.getAttribute('data-product-template') === 'true';
    var ids = (root.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
    var defaultId = root.getAttribute('data-default-id') || ids[0] || '';
    var secondaryId = root.getAttribute('data-secondary-id') || '';
    if (secondaryId === '0') secondaryId = '';
    var select = root.querySelector('.booking-form__select');
    var form = root.querySelector('.booking-form');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-booking-card]'));
    var linkSecondary = root.querySelector('[data-booking-secondary-link]');
    var linkBack = root.querySelector('[data-booking-secondary-back]');
    // Pickup mode's own header: present only when the template sets one; otherwise the main header stays in both modes.
    var heads = Array.prototype.slice.call(root.querySelectorAll('[data-booking-head]'));
    var block = { root: root, onProduct: onProduct, ids: ids, defaultId: defaultId, secondaryId: secondaryId, select: select, form: form, service: root.getAttribute('data-service') || 'studio', secondary: false };
    BV.blocks.push(block);

    // Pickup mode: the session-only sections of the template are hidden while the secondary session is selected.
    var prefix = root.getAttribute('data-section-prefix') || '';
    var hiddenKeys = (root.getAttribute('data-secondary-hidden') || '').split(',').filter(Boolean);
    function syncHiddenSections(secondary) {
      var inline = root.querySelector('[data-booking-secondary-style]');
      if (inline) inline.parentNode.removeChild(inline);
      hiddenKeys.forEach(function (key) {
        var el = document.getElementById('shopify-section-' + prefix + '__' + key);
        if (!el) return;
        if (secondary) el.setAttribute('data-booking-hidden', ''); else el.removeAttribute('data-booking-hidden');
      });
    }

    block.selected = function () { return select ? select.value : root.getAttribute('data-selected-id'); };

    function applyCard(id) {
      if (select) select.value = id;
      var secondary = !!secondaryId && id === secondaryId;
      cards.forEach(function (card) {
        var input = card.querySelector('input');
        if (input) input.checked = card.getAttribute('data-variant-id') === id;
        card.hidden = card.hasAttribute('data-secondary-card') !== secondary;
      });
      root.classList.toggle('booking-block--secondary', secondary);
      if (heads.length > 1) heads.forEach(function (head) { head.hidden = (head.getAttribute('data-booking-head') === 'secondary') !== secondary; });
      if (linkSecondary) linkSecondary.hidden = secondary;
      if (linkBack) linkBack.hidden = !secondary;
      root.setAttribute('data-selected-id', id);
      block.secondary = secondary;
      syncHiddenSections(secondary);
      if (block.cta && block.cta.refreshLabel) block.cta.refreshLabel();
    }
    block.applyCard = applyCard;

    function choose(id, source) {
      if (ids.indexOf(id) === -1) id = defaultId;
      if (onProduct && !DESIGN) {
        // Product templates reload so Liquid renders product.selected_variant as the selected card and option.
        if (id !== currentVariantParam()) { location.assign(urlWithVariant(id)); return; }
      }
      applyCard(id);
      if (!onProduct) replaceVariant(id);
      BV.track('booking_card_select', { variant_id: id, source: source || 'card' });
    }
    block.choose = choose;

    // Load: the page template applies a deep link after parse and drops ids that are not variants of the product;
    // the product templates already carry a valid ?variant= from the inline write, the bundle is the backstop.
    if (!DESIGN) {
      var param = currentVariantParam();
      if (onProduct) {
        if (!param || ids.indexOf(param) === -1) replaceVariant(block.selected());
      } else if (param) {
        if (ids.indexOf(param) !== -1) { if (param !== block.selected()) applyCard(param); }
        else { applyCard(defaultId); replaceVariant(null); }
      }
    }

    block.secondary = !!secondaryId && block.selected() === secondaryId;
    syncHiddenSections(block.secondary);

    cards.forEach(function (card) {
      var input = card.querySelector('input');
      if (!input) return;
      input.addEventListener('change', function () { if (input.checked) choose(input.value, 'card'); });
    });
    // The pickup link sits under the CTA and the cards above it change height: bring the block's top back into view.
    function toBlockTop() {
      var y = root.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
    if (linkSecondary) linkSecondary.addEventListener('click', function (e) { e.preventDefault(); choose(secondaryId, 'secondary-link'); toBlockTop(); });
    if (linkBack) linkBack.addEventListener('click', function (e) { e.preventDefault(); choose(defaultId, 'secondary-back'); toBlockTop(); });

    // The form never submits to /cart (KTD1); registered in the capture phase.
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); e.stopPropagation(); }, true);

    initCta(block);
    initSticky(block);
    fillMissingAlt();
    initMedia(block);
    if (!DESIGN) BV.track('booking_page_view', { service: block.service }, { once: true });
  }

  /* ---------- alt text fallback: images of the reused sections whose Files entry has no alt text ---------- */
  function fillMissingAlt() {
    Array.prototype.forEach.call(document.querySelectorAll('.shopify-section img:not([alt]), .shopify-section img[alt=""]'), function (img) {
      var section = img.closest('.shopify-section');
      var id = section ? section.id : '';
      var column = img.closest('.text-column, .grid__item, .text-with-icons__block, .feature-row__item');
      var heading = column ? column.querySelector('h2, h3, h4, .h3, .h4') : null;
      var text = heading ? heading.textContent.trim() : (section ? (section.querySelector('h1, h2, h3') || {}).textContent : '');
      if (/__map$/.test(id)) text = 'Carte du studio Avec Style';
      if (text) img.setAttribute('alt', String(text).trim());
    });
  }

  /* ---------- CTA controller (KTD1): one state machine for the in-block button, the sticky bar and the repeat CTAs ---------- */
  function initCta(block) {
    var root = block.root, form = block.form;
    if (!form) return;
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-booking-cta-button]'));
    var status = root.querySelector('[data-booking-cta-status]');
    var fallback = root.querySelector('[data-booking-cta-fallback]');
    var wrap = root.querySelector('[data-booking-cta-wrap]');
    var labelDefault = root.getAttribute('data-cta-label') || (buttons[0] ? buttons[0].textContent.trim() : '');
    var labelSecondary = root.getAttribute('data-cta-label-secondary') || labelDefault;
    var labelCta = function () { return block.secondary ? labelSecondary : labelDefault; };
    var labelLoading = root.getAttribute('data-cta-loading') || labelDefault;
    var cta = { state: 'loading', queued: null, readyTimer: null, openTimer: null, start: performance.now() };
    block.cta = cta;
    root.setAttribute('data-cta-state', 'loading');
    BV.marks.ctaInit = cta.start;

    var appointoButton = function () { return form.querySelector('[id^="appointo-btn"]'); };

    function setLabel(text, busy) {
      buttons.forEach(function (b) { b.textContent = text; b.setAttribute('aria-busy', busy ? 'true' : 'false'); });
      if (status) status.textContent = busy ? text : '';
    }
    function setState(next, detail) {
      if (cta.state === next) return;
      cta.state = next;
      root.setAttribute('data-cta-state', next);
      if (next === 'fallback') {
        if (wrap) wrap.classList.add('booking-cta-wrap--fallback');
        buttons.forEach(function (b) { b.hidden = true; });
        if (fallback) fallback.hidden = false;
        if (status) status.textContent = fallback ? fallback.textContent.trim() : '';
        BV.track('booking_widget_error', { reason: detail && detail.reason ? detail.reason : 'no_widget' }, { once: true });
      } else {
        if (wrap) wrap.classList.remove('booking-cta-wrap--fallback');
        buttons.forEach(function (b) { b.hidden = false; });
        if (fallback) fallback.hidden = true;
        setLabel(next === 'opening' ? labelLoading : labelCta(), next === 'opening');
      }
      emit('cta-state', { state: next, detail: detail });
    }
    // The card choice changed the base label (pickup mode): rewrite it unless the button is busy or hidden.
    cta.refreshLabel = function () {
      if (cta.state === 'opening' || cta.state === 'fallback') return;
      if (cta.state === 'loading' && cta.queued) return;
      setLabel(labelCta(), false);
    };

    function clearTimers() {
      if (cta.readyTimer) { clearTimeout(cta.readyTimer); cta.readyTimer = null; }
      if (cta.openTimer) { clearTimeout(cta.openTimer); cta.openTimer = null; }
    }

    function forward(source) {
      var target = appointoButton();
      if (!target) return false;
      setState('opening', { source: source });
      target.click();
      clearTimeout(cta.openTimer);
      cta.openTimer = setTimeout(function () {
        if (cta.state === 'opening' && !modalVisible()) setState('fallback', { reason: 'no_modal' });
      }, OPEN_TIMEOUT);
      return true;
    }

    function tap(source) {
      if (DESIGN) return;
      source = source || 'block';
      BV.track('booking_cta_click', { source: source, ms_since_load: Math.round(performance.now()) });
      emit('cta-tap', { source: source, state: cta.state });
      if (cta.state === 'opening') return;
      if (cta.state === 'ready' || cta.state === 'modalOpen') { forward(source); return; }
      if (cta.state === 'fallback' && appointoButton()) { setState('ready'); forward(source); return; }
      if (cta.state === 'fallback') {
        var target = fallback || wrap || root;
        if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      // Loading: show the loading state and replay once on ready.
      cta.queued = source;
      setLabel(labelLoading, true);
    }
    block.tap = tap;

    function onReady() {
      if (cta.state === 'ready' || cta.state === 'opening' || cta.state === 'modalOpen') return;
      clearTimeout(cta.readyTimer); cta.readyTimer = null;
      var t = appointoButton();
      if (t) { t.setAttribute('tabindex', '-1'); t.setAttribute('aria-hidden', 'true'); }
      setState('ready');
      BV.marks.ctaReady = performance.now();
      if (cta.queued) { var q = cta.queued; cta.queued = null; forward(q); }
    }

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        var source = button.getAttribute('data-booking-cta-source') || 'block';
        if (source === 'sticky') {
          var cards = root.querySelector('[data-booking-cards]') || root;
          cards.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        tap(source);
      });
    });

    if (DESIGN) { root.setAttribute('data-cta-state', 'static'); return; }

    // Readiness: the Appointo div exists inside the form. The observer stays attached until the div appears,
    // so a widget that arrives after the fallback also returns the CTA to ready (onReady accepts the fallback state).
    if (appointoButton()) onReady();
    else {
      var obs = new MutationObserver(function () { if (appointoButton()) { obs.disconnect(); onReady(); } });
      obs.observe(form, { childList: true, subtree: true });
      cta.readyTimer = setTimeout(function () {
        if (cta.state === 'loading') { setState('fallback', { reason: 'no_widget' }); }
      }, READY_TIMEOUT);
    }

    watchModal();
    BV.on('modal-open', function (d) {
      clearTimeout(cta.openTimer); cta.openTimer = null;
      var recovered = cta.state === 'fallback';
      setState('modalOpen');
      if (recovered) BV.track('booking_widget_recovered', { reason: 'no_modal' }, { once: true });
      if (!d || !d.restored) { BV.track('booking_calendar_open', { count: d ? d.count : undefined }); cta.slotTracked = false; }
    });
    // A slot picked inside the modal: once per open (the modal is injected, so the listener is delegated; one per page).
    if (!BV.slotListener) {
      BV.slotListener = true;
      document.addEventListener('click', function (e) {
        if (cta.slotTracked || !modal.open) return;
        var t = e.target && e.target.closest ? e.target.closest('#appointo-modal .appointo-slot') : null;
        if (!t) return;
        cta.slotTracked = true;
        BV.track('booking_slot_selected', { variant_id: block.selected() });
      }, true);
    }
    // The in-block CTA scrolled into view: once per page view, with the scroll depth.
    if ('IntersectionObserver' in window && buttons[0]) {
      var seen = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) { seen.disconnect(); BV.track('booking_scroll_cta', { scroll_px: Math.round(window.scrollY) }, { once: true }); } });
      }, { threshold: 0.5 });
      seen.observe(buttons[0]);
    }
    BV.on('modal-close', function () { if (cta.state === 'modalOpen' || cta.state === 'opening') setState('ready'); });

    // bfcache restore (KTD4): resync from the modal's visibility, cancel timers, drop the queued tap, count no new open.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      clearTimers(); cta.queued = null;
      syncModal(true);
      if (modal.open) setState('modalOpen'); else if (appointoButton()) { cta.state = 'loading'; onReady(); }
      emit('pageshow-restore', {});
    });
  }

  /* ---------- sticky bar (KTD4): hidden = in-block CTA visible OR modal open OR cookie banner visible OR desktop ---------- */
  function initSticky(block) {
    var root = block.root;
    var bar = root.querySelector('[data-booking-sticky]');
    if (!bar || DESIGN) return;
    var sentinel = root.querySelector('[data-booking-cta-sentinel]') || root.querySelector('[data-booking-cta-wrap]');
    var mq = window.matchMedia('(max-width: 768px)');
    var st = { ctaVisible: true, banner: false };
    block.stickyState = st;
    var banner = function () { return isVisible(document.getElementById('shopify-pc__banner')); };
    function update() {
      var fallback = block.cta && block.cta.state === 'fallback';
      var hidden = !mq.matches || st.ctaVisible || modal.open || st.banner || fallback;
      bar.hidden = hidden;
      document.documentElement.classList.toggle('booking-sticky-visible', !hidden);
      if (!hidden) document.documentElement.style.setProperty('--booking-sticky-height', bar.offsetHeight + 'px');
    }
    block.updateSticky = update;
    function measure() {
      if (!sentinel) { st.ctaVisible = false; return; }
      var top = sentinel.getBoundingClientRect().top;
      st.ctaVisible = top > 0 && top < window.innerHeight;
    }
    if ('IntersectionObserver' in window && sentinel) {
      new IntersectionObserver(function () { measure(); update(); }, { threshold: 0 }).observe(sentinel);
    }
    var ticking = false;
    var onScroll = function () { if (ticking) return; ticking = true; requestAnimationFrame(function () { ticking = false; measure(); update(); }); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    measure();
    st.banner = banner();
    function checkBanner() { var b = banner(); if (b !== st.banner) { st.banner = b; update(); } }
    new MutationObserver(checkBanner).observe(document.body, { childList: true, subtree: false, attributes: true, attributeFilter: ['class', 'style'] });
    var bannerEl = document.getElementById('shopify-pc__banner');
    if (bannerEl) new MutationObserver(checkBanner).observe(bannerEl, { attributes: true, childList: true, subtree: true });
    setInterval(checkBanner, 1000);
    if (mq.addEventListener) mq.addEventListener('change', update); else mq.addListener(update);
    BV.on('modal-open', update); BV.on('modal-close', update); BV.on('cta-state', update);
    update();
  }

  /* ---------- hero video (KTD9): desktop only, source attached after load, poster and pause control ---------- */
  function initMedia(block) {
    var root = block.root;
    var video = root.querySelector('[data-booking-video]');
    var button = root.querySelector('[data-booking-video-toggle]');
    if (!video || !button) return;
    // theme editor: poster only; shopify:section:load re-renders would otherwise accumulate window listeners
    if (DESIGN) { button.hidden = true; return; }
    var labels = { pause: root.getAttribute('data-video-pause-label') || 'Pause', play: root.getAttribute('data-video-play-label') || 'Lecture' };
    var desktop = window.matchMedia('(min-width: 769px)');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var started = false;
    function setPlaying(playing) {
      root.classList.toggle('booking-block--video-playing', playing);
      button.textContent = playing ? labels.pause : labels.play;
      button.setAttribute('aria-pressed', playing ? 'false' : 'true');
    }
    function tryPlay() {
      if (!video.getAttribute('src')) { video.setAttribute('src', video.getAttribute('data-src')); video.load(); }
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () { setPlaying(false); });
    }
    function start() {
      if (started || !desktop.matches || reduced.matches) return;
      started = true;
      button.hidden = false;
      setPlaying(false);
      tryPlay();
    }
    video.addEventListener('playing', function () { setPlaying(true); });
    video.addEventListener('pause', function () { setPlaying(false); });
    button.addEventListener('click', function () { if (video.paused) tryPlay(); else video.pause(); });
    // Registered before the early return so a viewport that becomes desktop later can start the clip (start() guards on started/desktop/reduced).
    if (desktop.addEventListener) desktop.addEventListener('change', function () {
      if (desktop.matches) { if (!reduced.matches) button.hidden = false; start(); }
      else if (!video.paused) video.pause();
    });
    if (!desktop.matches || reduced.matches) { button.hidden = true; return; }
    // After the load event, with a ceiling so a stalled third-party script does not hold the clip back for long.
    var kick = function () { setTimeout(start, 50); };
    if (document.readyState === 'complete') kick();
    else { window.addEventListener('load', kick); setTimeout(start, 8000); }
  }

  /* ---------- confirmation handoff (KTD10): one sessionStorage key, written at modal open, read on the confirmation page ---------- */
  var HANDOFF_KEY = 'bookingV2Handoff';
  function storage() { try { var s = window.sessionStorage; s.getItem(HANDOFF_KEY); return s; } catch (e) { return null; } }
  function readHandoff() {
    var s = storage(); if (!s) return null;
    var raw = null; try { raw = s.getItem(HANDOFF_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { var h = JSON.parse(raw); if (!h || typeof h !== 'object' || typeof h.ts !== 'number') { clearHandoff(); return null; } return h; } catch (e) { clearHandoff(); return null; }
  }
  function writeHandoff(h) { try { var s = storage(); if (s) s.setItem(HANDOFF_KEY, JSON.stringify(h)); } catch (e) { /* no storage: neutral block */ } }
  function clearHandoff() { try { var s = storage(); if (s) s.removeItem(HANDOFF_KEY); } catch (e) { /* ignore */ } }
  BV.handoff = { key: HANDOFF_KEY, read: readHandoff, write: writeHandoff, clear: clearHandoff };
  function serviceFor(block, variantId) { return block.secondaryId && String(variantId) === String(block.secondaryId) ? 'pickup' : block.service; }
  function recordHandoff(block, id) { writeHandoff({ service: serviceFor(block, id), variant_id: id, ts: Date.now(), consumed: false }); }
  BV.on('modal-open', function () {
    var block = BV.blocks[0]; if (!block || DESIGN) return;
    recordHandoff(block, block.selected());
  });
  // The dropdown inside the injected modal may change the booked session after the open (KD10).
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('#appointo-select-div select, #appointo-modal select[id*="variant"]')) return;
    var block = BV.blocks[0]; if (!block || DESIGN) return;
    if (t.value && block.ids.indexOf(String(t.value)) !== -1) recordHandoff(block, t.value);
  });
  function initConfirmation(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-booking-confirmation]'), function (section) {
      if (section.hasAttribute('data-booking-init')) return;
      section.setAttribute('data-booking-init', '');
      if (DESIGN) return;
      var ttl = (parseInt(section.getAttribute('data-ttl-minutes'), 10) || 120) * 60000;
      var h = readHandoff();
      var fresh = h && (Date.now() - h.ts) >= 0 && (Date.now() - h.ts) <= ttl;
      if (!fresh) { if (h) clearHandoff(); section.setAttribute('data-confirmation-shown', 'neutral'); return; }
      var service = ['studio', 'virtuel', 'pickup'].indexOf(h.service) !== -1 ? h.service : 'neutral';
      Array.prototype.forEach.call(section.querySelectorAll('[data-confirmation-service]'), function (b) { b.hidden = b.getAttribute('data-confirmation-service') !== service; });
      section.setAttribute('data-confirmation-shown', service);
      if (!h.consumed) {
        BV.track('booking_confirmed', { service: h.service, variant_id: h.variant_id }, { once: true });
        h.consumed = true; writeHandoff(h);
      }
    });
  }

  /* ---------- booking-cta sections: scroll mode calls the block's CTA function; link mode is a plain anchor ---------- */
  function initCtaSections(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-booking-cta]'), function (section) {
      if (section.hasAttribute('data-booking-init')) return;
      section.setAttribute('data-booking-init', '');
      var trigger = section.querySelector('[data-booking-cta-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        var source = trigger.getAttribute('data-booking-cta-source') || 'repeat';
        var block = BV.blocks[0];
        var target = document.getElementById('booking-block');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (block && block.tap) block.tap(source);
      });
    });
  }

  function initAll(scope) {
    var s = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(s.querySelectorAll('[data-booking-block]'), initBlock);
    initCtaSections(s);
    initConfirmation(s);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);
})();
